import SectionHeader from "../../../../util/component/SectionHeader";
import AdvancedFilter from "../../component/AdvancedFilter";
import {Box, Checkbox, Chip, Grid, ListItemText, MenuItem, Stack, TextField, Typography, useTheme} from "@mui/material";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import * as React from "react";
import {useContext, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {ColDef, GetRowIdParams, GridApi, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import EmptyState from "../../../../util/component/EmptyState";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import dayjs from "dayjs";
import {AgGridReact} from "ag-grid-react";
import * as Yup from "yup";
import {DatePicker} from "@mui/x-date-pickers";
import {useDetectionActions} from "../../service/useDetectionActions";
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {ICellRendererParams, ITooltipParams, ValueFormatterParams} from "ag-grid-community";
import {Detection, DetectionFilters} from "./constants/DetectionInterfaces";
import {PaymentStatuses} from "./constants/PaymentStatuses";
import DetectionDetailsModal from "./modals/DetectionDetailsModal";
import {useNavigate, useParams} from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import IconButton from "@mui/material/IconButton";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {useVoucherActions} from "../../service/useVoucherActions";
import { intToFloatCurrency } from "../../component/Utils";


const DetectionListPage = () => {
    const {subscriptionId} = useParams();
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'detection'});
    const {globalData} = useContext(GlobalContext);
    const {downloadInvoice}= useVoucherActions();
    const {dispatchGlobalData} = useContext(GlobalContext);
    const gridRef = useRef<AgGridReact>(null);
    const theme = useTheme();
    const defaultFilters : DetectionFilters = {
        legalEntity: globalData?.user?.legalEntities?.map((le: any) => le.name),
        dealer: globalData?.user?.dealers?.map((d: any) => d.name),
        detectionDate: null,
    };
    const [filters, setFilters] = useState<DetectionFilters>(defaultFilters);
    const [searchInput, setSearchInput] = useState<string>("");
    const {getDetections} = useDetectionActions();
    const [openDetailModal, setOpenDetailModal] = useState(false);
    const [selectedDetection, setSelectedDetection] = useState<Detection | undefined>(undefined);
    const navigate = useNavigate();


    const columns: ColDef[] = [
        {
            field: "tenantId",
            headerName: t('legalEntityField'),
            hide: globalData?.user?.legalEntities?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            },
            tooltipValueGetter: (params: any) => {
                return globalData?.user?.legalEntities.find((le: any) => le.name === params.data.tenantId)?.name
            }
        },
        {
            field: "dealerId",
            headerName: t('dealerField'),
            hide: globalData?.user?.dealers?.length <= 1,
            unSortIcon: true,
            cellRenderer: (params: ICellRendererParams) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            },
            tooltipValueGetter: (params: ITooltipParams) => {
                return globalData?.user?.dealers.find((le: any) => le.name === params.data.dealerId)?.name
            }
        },
        {
            field: "event",
            headerName: t('columns.event'),
            tooltipField: "event",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value === 'MONTH' ? params.value + ' ' + params.data.rate : params.value ?? '---'
        },
        {
            field: "detectionDate",
            headerName: t('columns.detectionDate'),
            tooltipField: "detectionDate",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value ? dayjs(params.value).format("lll") : ''
        },
        {
            field: "added",
            headerName: t('columns.added'),
            tooltipField: "added",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value ?? '---'
        },
        {
            field: "kmAccrued",
            headerName: t('columns.kmAccrued'),
            tooltipField: "kmAccrued",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value ?? '---'
        },
        {
            field: "kmDetected",
            headerName: t('columns.kmDetected'),
            tooltipField: "kmDetected",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value ?? '---'
        },
        {
            field: "kmConsumed",
            headerName: t('columns.kmConsumed'),
            tooltipField: "kmConsumed",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value ?? '---'
        },    
        {
            field: "amount",
            headerName: t('columns.amount'),
            tooltipField: "amount",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value ? intToFloatCurrency(params.value) : '---'
        },
        {
            field: "kmRemaining",
            headerName: t('columns.kmRemaining'),
            tooltipField: "kmRemaining",
            unSortIcon: true,
            valueFormatter: (params: ValueFormatterParams) => params.value ?? '---'
        },
        {
            field: "_actions",
            headerName: '',
            maxWidth: 130,
            lockPinned: true,
            pinned: 'right',
            lockPosition: 'right',
            sortable: false,
            cellRenderer: (params: ICellRendererParams) => {
                return (
                    <Stack direction={"row"} justifyContent={"center"}>
                        <ActionButtonWithTooltip disabled={params.data.invoiceId === undefined}
                                                 type={ActionTypes.invoice}  callback={() => onDownloadInvoice(+params.data.invoiceId)}
                                                 auth={'SCOPE_SM_DETECTION_DOWNLOAD_INVOICE'}
                        />
                        <ActionButtonWithTooltip
                            auth={'SCOPE_SM_VIEW_DETECTION_DETAIL'}
                            type={ActionTypes.details}
                            callback={() => {
                                setOpenDetailModal(true);
                                setSelectedDetection(params.data)
                            }}/>
                    </Stack>
                );
            }
        }
    ];

    const onDownloadInvoice = async (invoiceId: number) => {
        try {
            const {result} = await downloadInvoice(invoiceId);
            if (result) {
                let pdfWindow = window.open("");
                pdfWindow!.document.write(`<iframe width='100%' height='100%' src='data:application/pdf;base64, ${encodeURI(result)}'></iframe>`);
            }
        } catch (err) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("downloadError"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });

        }
    }

    const toolbar = {
        renderActions: () => <></>
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
            resizable: true,
            minWidth: 130
        },
        columnDefs: columns,
        noRowsOverlayComponentParams: {title: t('noRows')}
    };

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
    }, [filters]);

    function createDatasource(filters: any, api: GridApi) {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp = Object.assign({}, filters);
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                let data = await getDetections(filterTemp,subscriptionId!);
                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements
                });
                if (data.totalElements === 0) {
                    api.showNoRowsOverlay();
                } else {
                    api.hideOverlay();
                }
            }
        };
    };

    const fields = {
        detectionDate: (field: any) => (
            <DatePicker
                onChange={field.onChange}
                value={field.value}
                label={t('columns.detectionDate')}
                renderInput={(params) =>
                    <TextField
                        {...params}
                        fullWidth
                    />}
                inputFormat={"L"}>
            </DatePicker>
        )
    }

    const validationObj = {
        status: Yup.array(),
        paymentStatus: Yup.array().of(Yup.string()).min(0),
        detectionDate: Yup.date().nullable()
    }

    return (
        <>
            <SectionHeader title={t('listHeader.title')} subtitle={t('listHeader.subtitle')+ subscriptionId}/>
            <Box sx={{padding:'16px', display:'flex', justifyContent:'flex-start', alignItems:'center'}}>
                <IconButton onClick={()=>{navigate('/salesmarketing/subscriptionContractslist')}}>
                    <ArrowBackIcon color={'primary'}/>
                </IconButton>
                <Typography variant={'h6'} fontWeight={"bold"} color={"primary"}>
                    {t('listHeader.back')}
                </Typography>
            </Box>
            <AdvancedFilter validationObj={validationObj} defaultValues={defaultFilters} fields={fields}
                            searchInput={searchInput} setSearchInput={setSearchInput} filters={filters}
                            setFilters={setFilters}/>
            <Grid container>
                <Grid item xs={12} sx={{padding: "16px"}}>
                    <AgGridCustomTable gridOptions={gridOptions} gridRef={gridRef} toolbar={toolbar}
                                       noRowsOverlayComponent={noRowsOverlayComponent}
                                       onGridReady={(params) => {
                                           params.api.setServerSideDatasource(createDatasource(filters, gridRef.current!.api));
                                       }}/>
                </Grid>
            </Grid>
            <DetectionDetailsModal detection={selectedDetection} open={openDetailModal} handleClose={()=>{setOpenDetailModal(false)}}/>
        </>
    );
}
export default DetectionListPage;